/**
 * Typed errors thrown by ApiClient. Backend wire shape:
 *   { "error": { "code": "pin_expired", "message": "..." } }
 *
 * Every error has a stable `code` so screens can branch on it without
 * sniffing strings. Use `API_ERROR_CODE.*` instead of string literals
 * everywhere — the wire-format snapshot test in
 * `__tests__/errors.test.ts` will scream if any value drifts from
 * what the backend emits.
 */

export const API_ERROR_CODE = {
  // Auth / PIN flow
  PinExpired: 'pin_expired',
  PinLocked: 'pin_locked',
  PinInvalid: 'pin_invalid',
  InvalidContact: 'invalid_contact',
  // PIN login with an address the account moved away from; the message
  // points to the phone or new email.
  EmailChanged: 'email_changed',
  // Carrier refused the text outright and no retry will change that. One
  // code for every reason on purpose: the backend collapses STOP / invalid /
  // unsupported-region into this, because `unsubscribed` alone would tell an
  // unauthenticated caller that a number has been on our list.
  Undeliverable: 'undeliverable',
  // Authorization
  Unauthenticated: 'unauthenticated',
  Forbidden: 'forbidden',
  // Generic HTTP
  NotFound: 'not_found',
  Unprocessable: 'unprocessable',
  RateLimited: 'rate_limited',
  ServerError: 'server_error',
  // Transport
  NetworkError: 'network_error',
  Timeout: 'timeout',
  Unknown: 'unknown',
  // Deals / advance flow
  DealUnavailable: 'deal_unavailable',
  DealFull: 'deal_full',
  StepNotFound: 'step_not_found',
  StepHasNoCta: 'step_has_no_cta',
  StepOutOfOrder: 'step_out_of_order',
  // Season of Gifting claim flow
  GiftingDisabled: 'gifting_disabled',
  GiftSoldOut: 'sold_out',
  GiftAlreadyClaimed: 'already_claimed',
  // Profile: the email belongs to (or used to belong to) another account.
  EmailTaken: 'email_taken',
} as const;
export type ApiErrorCode = (typeof API_ERROR_CODE)[keyof typeof API_ERROR_CODE];

export interface ApiErrorBody {
  code: ApiErrorCode | string;
  message?: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly code: ApiErrorCode | string;
  readonly status: number;
  readonly details?: Record<string, unknown>;

  constructor(args: {
    code: ApiErrorCode | string;
    message: string;
    status: number;
    details?: Record<string, unknown>;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.code = args.code;
    this.status = args.status;
    this.details = args.details;
  }
}

export class UnauthenticatedError extends ApiError {
  constructor(message = 'Your session has expired. Please sign in again.') {
    super({ code: API_ERROR_CODE.Unauthenticated, message, status: 401 });
    this.name = 'UnauthenticatedError';
  }
}

export class RateLimitError extends ApiError {
  readonly retryAfterSeconds: number;

  constructor(args: { message?: string; retryAfterSeconds: number }) {
    super({
      code: API_ERROR_CODE.RateLimited,
      message: args.message ?? 'Too many requests. Please wait and try again.',
      status: 429,
    });
    this.name = 'RateLimitError';
    this.retryAfterSeconds = args.retryAfterSeconds;
  }
}

export class ValidationError extends ApiError {
  constructor(args: { code?: string; message: string; details?: Record<string, unknown> }) {
    super({
      code: args.code ?? API_ERROR_CODE.Unprocessable,
      message: args.message,
      status: 422,
      details: args.details,
    });
    this.name = 'ValidationError';
  }
}

export class ServerError extends ApiError {
  constructor(args: { message?: string; status: number }) {
    super({
      code: API_ERROR_CODE.ServerError,
      message: args.message ?? 'Something went wrong on our end. Please try again.',
      status: args.status,
    });
    this.name = 'ServerError';
  }
}

export class NetworkError extends ApiError {
  constructor(message = 'You appear to be offline. Check your connection.') {
    super({ code: API_ERROR_CODE.NetworkError, message, status: 0 });
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends ApiError {
  constructor(message = 'The request took too long. Please try again.') {
    super({ code: API_ERROR_CODE.Timeout, message, status: 0 });
    this.name = 'TimeoutError';
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** The server's own copy for this failure (e.g. Voltr's "application wasn't approved"); `fallback` only when there is none. */
export function messageFor(err: unknown, fallback: string): string {
  return isApiError(err) && err.message ? err.message : fallback;
}
